import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import usePostData from "../../../../../../CustomHooks/useAxios/usePostData";
import { FACTORY_CHECKPOINT_UPDATE } from "../../../../../../api/allApi_List";
import { setJobCardData } from "../../../../../../redux/jobCardDataSlice";
import CheckpointField from "./CheckpointField";
import CheckpointSubmit from "./CheckpointSubmit";
import CheckpointCompleted from "./CheckpointCompleted";
import CheckpointHeader from "./CheckpointHeader";
import { areCheckpointDependenciesSatisfied } from "../../../../utils/jobCardDependencyUtils";
import DiscardMessage from "./DiscardMessage";
import DiscardedSuccess from "./DiscardedSuccess";
import JobCardSuccessView from "../../JobCardSuccessView";
import ErrorDisplay from "../../../../../../components/UI/ErrorDisplay";
import { FACTORY_CHECKPOINT_PERMISSIONS } from "../../../../constants/checkpointPermissions";
import UsePermissionsClaim from "../../../../../../CustomHooks/UsePermissionsClaim";

const CheckpointItem = ({
  checkpoint,
  updatedCheckPoints,
  discardCondition,
  actionHidden,
  resultMessage,
}) => {
  const [value, setValue] = useState("");
  const { executePost, loading, formError, setFormError, successResponse } =
    usePostData();
  const jobCardData = useSelector((state) => state.jobCard.data);
  const dispatch = useDispatch();

  // Current Checkpoint data
  const { id, name, description, dependsOnIds } = checkpoint;
  // Get Discarded Information
  const {
    id: discardId,
    discardOn,
    message: discardMessage,
  } = discardCondition || {};

  // Job card Data
  const {
    cylinderId,
    checkpointIds,
    cylinder: { hydroTestingDate },
    stageId,
  } = jobCardData;

  // Get Permission by ID and Check
  const { checkPermission } = UsePermissionsClaim();
  const checkPerm = checkPermission(FACTORY_CHECKPOINT_PERMISSIONS[id]);

  // find Current Checkpoint updated data
  const updatedCheckPoint = updatedCheckPoints?.find(
    (cp) => cp.checkpointId === id,
  );
  const { resultVal, status } = updatedCheckPoint ?? {};

  // check if current checkpoint is completed
  const isCompleted = status === 1;

  // Verify whether all required dependent checkpoints
  // are completed before enabling this checkpoint
  const isDependencyValid = areCheckpointDependenciesSatisfied(
    dependsOnIds,
    checkpointIds,
  );

  // Final condition to enable the checkpoint:
  // 1. User must have permission (checkPerm)
  // AND
  // 2. Either all dependencies are satisfied
  const isCheckpointEnabled = isCompleted || (checkPerm && isDependencyValid);
  const isSuccessResponse = successResponse?.status === 1;

  //API calling for checkpoint action
  const sendData = async () => {
    const updateStateOnResponse = (data) => {
      dispatch(setJobCardData(data));
    };
    executePost(
      {},
      `${FACTORY_CHECKPOINT_UPDATE}${id}/${cylinderId}/${value}`,
      updateStateOnResponse,
    );
  };

  return (
    <div
      className={`group bg-white rounded-xl p-4 py-2 max-md:p-2 shadow-sm hover:shadow-md transition border-1 relative ${borderStyle[status]}`}
    >
      {/* Show discarded message if discarded is required AND Hide on when any checkpoint actions is hidden*/}
      {discardId === id && !actionHidden?.hideOn && (
        <DiscardMessage
          discardOn={discardOn === value}
          message={discardMessage}
        />
      )}

      {/* Show Checkpoint action hidden message */}
      {actionHidden?.hideOn && (
        <p className="text-xs text-blue-50 pb-1">{actionHidden?.msg}</p>
      )}
      {/* Header */}
      <CheckpointHeader name={name} description={description} />

      {/* show actions buttons only when no action hidden is required  */}
      {!actionHidden?.hideOn && (
        <div className="flex justify-between items-center">
          {/* Input */}
          <div
            className={`mt-2 md:my-4 w-full ${isCheckpointEnabled ? "" : "disable-bx"}`}
          >
            <CheckpointField
              checkpoint={checkpoint}
              value={resultVal && id !== 11 ? resultVal : value}
              onChange={setValue}
              readOnly={isCompleted}
            />
          </div>
          {/* Actions */}
          {((!resultVal && id !== 11) ||
            (id === 11 && hydroTestingDate !== value)) && (
            <CheckpointSubmit
              value={value}
              loading={loading}
              onSubmit={sendData}
            />
          )}
          {/* Checkpoint completed status */}
          {((isCompleted && id !== 11) ||
            (id === 11 && hydroTestingDate === value)) && (
            <CheckpointCompleted />
          )}
        </div>
      )}
            {resultMessage && id === 10 && (
        <p className="text-xs text-blue-50 pb-1">{resultMessage}</p>
      )}

      {/* Discarded Success */}
      {isSuccessResponse && discardOn === value && (
        <DiscardedSuccess cylinderSerialNo={cylinderId} />
      )}
      {/* show restart message when Hydro testing performed is ok */}
      {isSuccessResponse && id === 22 && value === "ok" && (
        <JobCardSuccessView message="Job card has been restart" />
      )}
      {isSuccessResponse && stageId === 8 && (
        <JobCardSuccessView
          message="Job card successfully created!"
          checkIcon={true}
        />
      )}
      {formError && (
        <div className="flex">
          <ErrorDisplay
            message={formError}
            closeAction={() => setFormError("")}
          />
        </div>
      )}
    </div>
  );
};

export default CheckpointItem;

export const borderStyle = {
  0: "border-gray-200",
  1: "border-teal-100",
};
